import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md font-normal transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 text-13 font-medium",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 text-13 font-medium",
        outline:
          "border border-input bg-card hover:bg-accent hover:text-accent-foreground text-13 font-medium",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 text-13 font-medium",
        ghost: "hover:bg-accent hover:text-accent-foreground text-13 font-medium",
        link: "text-primary underline-offset-4 hover:underline text-13 font-medium",
      },
      size: {
        default: "h-7 px-2 text-13",
        sm: "h-6 px-2 text-xs",
        lg: "h-9 px-6 text-sm",
        icon: "h-7 w-7",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  // Legacy props for backward compatibility
  fullWidth?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({
    className,
    variant,
    size,
    asChild = false,
    // Legacy props
    fullWidth,
    loading,
    leftIcon,
    rightIcon,
    children,
    disabled,
    ...props
  }, ref) => {
    const Comp = asChild ? Slot : "button"

    const isDisabled = disabled || loading;

    // Build className with legacy support
    const classes = cn(
      buttonVariants({ variant, size }),
      fullWidth && "w-full",
      loading && "relative",
      className
    );
    
    return (
      <Comp
        className={classes}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {loading && (
          <span className="absolute inset-0 flex items-center justify-center">
            <svg 
              className="animate-spin h-4 w-4" 
              viewBox="0 0 14 14" 
              fill="none"
            >
              <circle
                cx="7"
                cy="7"
                r="5.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray="26 26"
                strokeDashoffset="26"
              />
            </svg>
          </span>
        )}
        
        <span className={cn("flex items-center gap-1", loading && "invisible")}>
          {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
          {children}
          {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
        </span>
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }